import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as actions from '../store/actions';

import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, List, Modal, Select, Space, Spin, Typography } from 'antd';
import { isArray, isEmpty, isString } from 'lodash';

import { AppDispatch, RootState } from '../../../store';

import { renderNoDataOrSpinner } from '../../../shared/utils';

import { PhoneGroup, SaveTwilioAssignedAgentsRequest } from '../models';

const { Text } = Typography;

export type AssignAgentProps = {
  open: boolean;
  onAssignAgentClose: () => void;
  accountSid: string;
  authToken: string;
};

const AssignAgent = ({ open, onAssignAgentClose, accountSid, authToken }: AssignAgentProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const didMount = useRef(false);

  const [initiatingDone, setInitiatingDone] = useState<boolean>(false);
  const [twilioAssignedAgentsLocal, setTwilioAssignedAgentsLocal] = useState<Array<PhoneGroup>>([]);

  const { twilioAssignedAgents, twilioAssignedAgentsLoading, allTwilioPhones, allTwilioPhonesLoading, saveTwilioAssignedAgentsResultLoading } =
    useSelector((state: RootState) => state.twilio);

  useEffect(() => {
    if (!didMount.current) {
      Promise.all([dispatch(actions.getAllTwilioPhones({ accountSid, authToken })), dispatch(actions.getTwilioAssignedAgents())]).then((values) => {
        setInitiatingDone(true);
      });
      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    setTwilioAssignedAgentsLocal(twilioAssignedAgents ?? []);
  }, [twilioAssignedAgents, twilioAssignedAgentsLoading]);

  const onClose = () => {
    dispatch(actions.setTwilioAssignedAgents(undefined));
    onAssignAgentClose();
  };

  const onOk = () => {
    if (isArray(twilioAssignedAgentsLocal) && !isEmpty(twilioAssignedAgentsLocal)) {
      let users = twilioAssignedAgentsLocal.filter(
        (x) => (x.isNew && isString(x.phoneNumber) && !isEmpty(x.phoneNumber)) || (x.isDirty && isString(x.id) && !isEmpty(x.id)),
      );

      dispatch(actions.saveTwilioAssignedAgents({ phoneGroupUsers: users } as SaveTwilioAssignedAgentsRequest)).then(onClose);
    }
  };

  const hasUpdates = () => {
    if (isArray(twilioAssignedAgentsLocal) && !isEmpty(twilioAssignedAgentsLocal)) {
      var notFilled = twilioAssignedAgentsLocal.filter((x) => x.isNew && !(isString(x.phoneNumber) && !isEmpty(x.phoneNumber)));

      if (notFilled.length > 0) {
        return false;
      }

      let users = twilioAssignedAgentsLocal.filter(
        (x) => (x.isNew && isString(x.phoneNumber) && !isEmpty(x.phoneNumber)) || (x.isDirty && isString(x.id) && !isEmpty(x.id)),
      );

      return users.length > 0;
    }
  };

  const onSelectPhoneChange = (phoneNumber: string | undefined, twilioAssignedAgent: PhoneGroup, index: number) => {
    const newTwilioAssignedAgent = { ...twilioAssignedAgent } as PhoneGroup;

    newTwilioAssignedAgent.phoneNumber = phoneNumber;

    if (isString(twilioAssignedAgent.id) && !isEmpty(twilioAssignedAgent.id) && !twilioAssignedAgent.isDirty) {
      newTwilioAssignedAgent.isDirty = true;
    }

    const newTwilioAssignedAgents = [...twilioAssignedAgentsLocal];
    newTwilioAssignedAgents[index] = newTwilioAssignedAgent;

    setTwilioAssignedAgentsLocal(newTwilioAssignedAgents);
  };

  const isAddButtonDisabled = (twilioAssignedAgent: PhoneGroup) => {
    if (!isEmpty(twilioAssignedAgentsLocal) && !isEmpty(allTwilioPhones)) {
      const hasPhoneNumber = isString(twilioAssignedAgent.phoneNumber) && !isEmpty(twilioAssignedAgent.phoneNumber);

      if (hasPhoneNumber) {
        let hasMorePhoneNumbers = (allTwilioPhones ?? []).filter((x) => x !== twilioAssignedAgent.phoneNumber).length > 0;

        if (hasMorePhoneNumbers) {
          const allAssignedUserPhoneNumbers = twilioAssignedAgentsLocal
            .filter((x) => x.assignUserId === twilioAssignedAgent.assignUserId)
            .map((x) => x.phoneNumber);

          hasMorePhoneNumbers = (allTwilioPhones ?? []).filter((x) => !allAssignedUserPhoneNumbers.includes(x)).length > 0;

          if (hasMorePhoneNumbers) {
            let newEmptyUsers = twilioAssignedAgentsLocal.filter(
              (x) => x.assignUserId === twilioAssignedAgent.assignUserId && x.isNew && !(isString(x.phoneNumber) && !isEmpty(x.phoneNumber)),
            );

            return newEmptyUsers.length > 0;
          }
        }
      }
    }

    return true;
  };

  const isDeleteButtonDisabled = (twilioAssignedAgent: PhoneGroup) => {
    return (
      !isEmpty(twilioAssignedAgentsLocal) && twilioAssignedAgentsLocal.filter((x) => x.assignUserId === twilioAssignedAgent.assignUserId).length === 1
    );
  };

  const onDeleteClick = (twilioAssignedAgent: PhoneGroup, index: number) => {
    let newTwilioAssignedAgents = [...twilioAssignedAgentsLocal];

    newTwilioAssignedAgents = newTwilioAssignedAgents.filter((x) => x !== twilioAssignedAgent);

    setTwilioAssignedAgentsLocal(newTwilioAssignedAgents);

    if (!isEmpty(twilioAssignedAgent.id) && isString(twilioAssignedAgent.id)) {
      onSelectPhoneChange(undefined, twilioAssignedAgent, index);
    }
  };

  const onAddPhoneClick = (twilioAssignedAgent: PhoneGroup, index: number) => {
    let newTwilioAssignedAgents = twilioAssignedAgentsLocal.slice();

    const newItem = { ...twilioAssignedAgent } as PhoneGroup;

    newItem.id = undefined;
    newItem.phoneNumber = undefined;
    newItem.isNew = true;

    newTwilioAssignedAgents.splice(index + 1, 0, newItem);

    setTwilioAssignedAgentsLocal(newTwilioAssignedAgents);
  };

  const getTwilioPhone = (twilioAssignedAgent: PhoneGroup) => {
    const allAssignedUserPhoneNumbers = twilioAssignedAgentsLocal
      .filter((x) => x.assignUserId === twilioAssignedAgent.assignUserId)
      .map((x) => x.phoneNumber);

    return (allTwilioPhones ?? []).filter((x) => !allAssignedUserPhoneNumbers.includes(x));
  };

  return (
    <Modal
      title="Assign Agent"
      open={open}
      onCancel={onClose}
      onClose={onClose}
      onOk={onOk}
      okButtonProps={{
        loading: saveTwilioAssignedAgentsResultLoading,
        disabled: !hasUpdates(),
      }}
      width={1000}
    >
      <div className="twilio-account-edit-component-assign-agent-wrapper">
        <Spin spinning={twilioAssignedAgentsLoading || !initiatingDone}>
          <List
            className="twilio-account-edit-component-assign-agent-wrapper-list"
            size="small"
            bordered
            dataSource={twilioAssignedAgentsLocal ?? []}
            renderItem={(twilioAssignedAgent, index) => (
              <List.Item>
                <Text>{twilioAssignedAgent.displayName}</Text>

                <Text>{twilioAssignedAgent.userName}</Text>

                <Space>
                  <Select
                    placeholder="Select a phone"
                    notFoundContent={renderNoDataOrSpinner(allTwilioPhonesLoading)}
                    loading={allTwilioPhonesLoading}
                    defaultValue={twilioAssignedAgent.phoneNumber}
                    value={twilioAssignedAgent.phoneNumber}
                    onChange={(phoneNumber) => onSelectPhoneChange(phoneNumber, twilioAssignedAgent, index)}
                    allowClear
                  >
                    {isArray(allTwilioPhones)
                      ? getTwilioPhone(twilioAssignedAgent).map((phone) => (
                          <Select.Option key={phone} value={phone}>
                            {phone ?? ''}
                          </Select.Option>
                        ))
                      : null}
                  </Select>

                  <Button
                    onClick={() => onAddPhoneClick(twilioAssignedAgent, index)}
                    disabled={isAddButtonDisabled(twilioAssignedAgent)}
                    size="small"
                    icon={<CopyOutlined />}
                  />

                  <Button
                    onClick={() => onDeleteClick(twilioAssignedAgent, index)}
                    disabled={isDeleteButtonDisabled(twilioAssignedAgent)}
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                  />
                </Space>
              </List.Item>
            )}
          />
        </Spin>
      </div>
    </Modal>
  );
};

export default AssignAgent;
